import { Interface } from 'ethers'

/**
 * Read-only on-chain probe of a Safe address. Used by the OnboardingCard to
 * tell the visitor *why* their Safe isn't recognised — is it not a Safe at
 * all, is it a Safe with modules but ours isn't one, etc.
 *
 * v1: mainnet only. When safeward goes multi-chain, take the chain as a
 * param and use that chain's RPC list.
 *
 * Why a separate RPC list (not the shared `proxyRpc` / `PUBLIC_RPCS`):
 * cloudflare-eth.com — the default we route dApp calls to — silently
 * returns "Internal error" (-32603) for some `eth_getCode` / `eth_call`
 * requests. That's fine for dApps (they retry, they cache, the user can
 * usually paper over it) but catastrophic for the onboarding probe where a
 * single failed call means the visitor sees "couldn't reach mainnet" on a
 * Safe that's clearly there. We try multiple read-only public RPCs in
 * sequence and only surface "unreachable" if all of them fail.
 */
const PROBE_RPCS = [
  'https://ethereum-rpc.publicnode.com',
  'https://eth.llamarpc.com',
  'https://cloudflare-eth.com',
]

async function multiRpcCall(method: string, params: unknown[]): Promise<unknown> {
  let lastError: Error | null = null
  for (const url of PROBE_RPCS) {
    try {
      const resp = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ jsonrpc: '2.0', id: 1, method, params }),
      })
      if (!resp.ok) {
        lastError = new Error(`${url} returned HTTP ${resp.status}`)
        continue
      }
      const json = (await resp.json()) as {
        result?: unknown
        error?: { code: number; message: string }
      }
      if (json.error) {
        // -32603 (Internal error) is the cloudflare flake mode — try the
        // next RPC. Real reverts come back as code 3 / -32000 with
        // message "execution reverted"; surface those immediately so the
        // caller can classify them as "not a Safe" instead of looping.
        if (json.error.code === -32603) {
          lastError = new Error(`${url}: ${json.error.message}`)
          continue
        }
        const err = new Error(json.error.message) as Error & { code?: number }
        err.code = json.error.code
        throw err
      }
      return json.result
    } catch (err) {
      // Network-level failures (CORS, DNS, fetch errors) — fall through
      // to the next RPC. Re-thrown JSON-RPC errors (real reverts) bypass
      // this catch via the outer throw.
      if (err instanceof Error && (err as Error & { code?: number }).code !== undefined) {
        throw err
      }
      lastError = err instanceof Error ? err : new Error(String(err))
    }
  }
  throw lastError ?? new Error('All probe RPCs failed')
}

const SAFE_IFACE = new Interface([
  'function getModulesPaginated(address start, uint256 pageSize) view returns (address[] array, address next)',
  'function getOwners() view returns (address[])',
  'function VERSION() view returns (string)',
])

const SENTINEL = '0x0000000000000000000000000000000000000001'

export type SafeProbe =
  | {
      kind: 'safeward-main'
      /** All modules enabled on the input address. */
      modules: string[]
      /** The specific module that has the Guardian as an owner — this is
       *  the SafeWard Module Safe we should query for pending txs. */
      moduleSafe: string
      version: string | null
    }
  | {
      kind: 'safe-without-safeward'
      modules: string[]
      version: string | null
    }
  | { kind: 'not-a-safe'; reason: string }
  | { kind: 'unreachable'; error: string }

export async function probeSafe(
  address: string,
  /** Guardian EOA from /v1/health. We declare any enabled module that's a
   *  Safe with this address as an owner to be SafeWard's Module Safe. Pass
   *  null when the Guardian isn't reachable — we'll fall back to "no
   *  SafeWard detected" but still report the Safe + its modules. */
  guardianAddress: string | null,
): Promise<SafeProbe> {
  // Cheap pre-check: if there's no code at the address it's an EOA (or
  // self-destructed contract). No need to fire eth_call against it.
  let code: string
  try {
    code = (await multiRpcCall('eth_getCode', [address, 'latest'])) as string
  } catch (err) {
    return { kind: 'unreachable', error: (err as Error).message }
  }
  if (!code || code === '0x' || code === '0x0') {
    return { kind: 'not-a-safe', reason: 'no contract code at this address' }
  }

  // getModulesPaginated is the cheapest "is this a Safe?" probe — every Safe
  // version has it, non-Safes will revert (which surfaces as a JSON-RPC
  // error code we throw on). Network-level "internal error" loops through
  // the fallback RPCs first so we don't mis-label a flaky RPC as "not a
  // Safe".
  const callData = SAFE_IFACE.encodeFunctionData('getModulesPaginated', [
    SENTINEL,
    100,
  ])
  let modulesResult: string
  try {
    modulesResult = (await multiRpcCall('eth_call', [
      { to: address, data: callData },
      'latest',
    ])) as string
  } catch (err) {
    return { kind: 'not-a-safe', reason: (err as Error).message }
  }
  if (!modulesResult || modulesResult === '0x') {
    return { kind: 'not-a-safe', reason: 'getModulesPaginated returned no data' }
  }

  let modules: string[]
  try {
    const decoded = SAFE_IFACE.decodeFunctionResult(
      'getModulesPaginated',
      modulesResult,
    )
    modules = (decoded[0] as string[]).map((m) => m.toLowerCase())
  } catch {
    return {
      kind: 'not-a-safe',
      reason: 'response is not a Safe modules tuple',
    }
  }

  // Best-effort VERSION — useful for the UI ("Safe v1.4.1") but not
  // required. Older Safes expose it; some forks don't.
  let version: string | null = null
  try {
    const verData = SAFE_IFACE.encodeFunctionData('VERSION', [])
    const verResult = (await multiRpcCall('eth_call', [
      { to: address, data: verData },
      'latest',
    ])) as string
    if (verResult && verResult !== '0x') {
      version = SAFE_IFACE.decodeFunctionResult('VERSION', verResult)[0] as string
    }
  } catch {
    /* not fatal */
  }

  // SafeWard detection: which module (if any) is a Safe whose owners
  // include the Guardian? That's the SafeWard Module Safe — we can route
  // pending txs through it. We try modules in order and stop on first hit;
  // most Safes have ≤ 2 modules so the cost is bounded.
  if (guardianAddress) {
    const guardianLc = guardianAddress.toLowerCase()
    const ownersData = SAFE_IFACE.encodeFunctionData('getOwners', [])
    for (const moduleAddr of modules) {
      try {
        const ownersResult = (await multiRpcCall('eth_call', [
          { to: moduleAddr, data: ownersData },
          'latest',
        ])) as string
        if (!ownersResult || ownersResult === '0x') continue
        const owners = SAFE_IFACE.decodeFunctionResult(
          'getOwners',
          ownersResult,
        )[0] as string[]
        if (owners.some((o) => o.toLowerCase() === guardianLc)) {
          return { kind: 'safeward-main', modules, moduleSafe: moduleAddr, version }
        }
      } catch {
        // module isn't a Safe (or call failed) — try the next one
      }
    }
  }
  return { kind: 'safe-without-safeward', modules, version }
}
